import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useToast } from './common/ToastContainer';

/**
 * Reads backend OAuth return query (?procoreConnected=true|false) once per navigation,
 * shows a toast, then strips params (P-009 / LD-6).
 *
 * ISS-223 (LD-7): the backend callback may also append
 * `?pendingCompanySelection=true` when the user has just OAuth-connected
 * AND either (a) no companies[] are bound yet, or (b) the user has
 * access to multiple Procore companies. In that case we show an
 * additional info-toast prompting the user to pick companies.
 */
export default function ProcoreOAuthCallbackToast() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSuccess, showError, showInfo } = useToast();
  const handledKeyRef = useRef(null);

  useEffect(() => {
    const procoreConnected = searchParams.get('procoreConnected');
    if (!procoreConnected) {
      handledKeyRef.current = null;
      return;
    }

    const dedupeKey = `${window.location.pathname}:${searchParams.toString()}`;
    if (handledKeyRef.current === dedupeKey) {
      return;
    }
    handledKeyRef.current = dedupeKey;

    if (procoreConnected === 'true') {
      showSuccess('Procore', 'Procore connected successfully.');
      // ISS-223 LD-7: prompt the user to pick which Procore companies
      // to bind when the backend signals pendingCompanySelection.
      const pendingCompanySelection = searchParams.get('pendingCompanySelection') === 'true';
      if (pendingCompanySelection && typeof showInfo === 'function') {
        showInfo(
          'Procore',
          'Select the companies you want to sync below to begin pulling data.'
        );
      }
    } else {
      const errDetail =
        searchParams.get('procoreError') ||
        searchParams.get('error') ||
        'Connection failed';
      showError(
        'Procore connection failed',
        typeof errDetail === 'string' ? errDetail : 'Connection failed'
      );
    }

    const next = new URLSearchParams(searchParams);
    next.delete('procoreConnected');
    next.delete('procoreError');
    next.delete('error');
    next.delete('pendingCompanySelection');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, showSuccess, showError, showInfo]);

  return null;
}
